import React, { useEffect, useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styles from './bidform.module.css';
import modalStyles from '../../styles/modal.module.css';
import { getContacts } from '../../services/api';
import { useAuth } from '../../contexts/authcontext';

function BidForm({ onSubmit, onCancel, bidType, isSubmitting, bid, isModal = false }) {
  const isEditMode = !!bid;
  const [showClientAddressBook, setShowClientAddressBook] = useState(false);
  const [clients, setClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [filters, setFilters] = useState({ search: '' });
  const { user } = useAuth();
  
  console.log('BidForm: Rendering with bidType:', bidType, 'isEditMode:', isEditMode);
  
  const { register, handleSubmit, control, formState: { errors }, reset, setValue } = useForm({
    defaultValues: bid ? {
      ...bid,
      DateDue: bid.DateDue ? new Date(bid.DateDue).toISOString().split('T')[0] : '',
      DateIssued: bid.DateIssued ? new Date(bid.DateIssued).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      Status: bid.BidStatus || 'Open' // Map BidStatus to Status for the form
    } : {
      JobNumber: '',
      ClientName: '',
      JobName: '',
      JobDescription: '',
      AssignedTo: '',
      Contact: '',
      Status: 'Open', // Use Status instead of BidStatus for consistency
      BidAmount: '',
      DateDue: '',
      DateIssued: new Date().toISOString().split('T')[0], // Default to today's date in YYYY-MM-DD format
    }
  });

  // Reset form if bidType changes or if the bid prop changes
  useEffect(() => {
    if (bid) {
      // For existing bids, format the dates to YYYY-MM-DD if they exist
      // and map BidStatus to Status for the form
      const formattedBid = {
        ...bid,
        DateDue: bid.DateDue ? new Date(bid.DateDue).toISOString().split('T')[0] : '',
        DateIssued: bid.DateIssued ? new Date(bid.DateIssued).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        Status: bid.BidStatus || 'Open' // Map BidStatus to Status for the form
      };
      reset(formattedBid);
    } else {
      reset({
        JobNumber: '',
        ClientName: '',
        JobName: '',
        JobDescription: '',
        AssignedTo: '',
        Contact: '',
        Status: 'Open', // Use Status instead of BidStatus for consistency
        BidAmount: '',
        DateDue: '',
        DateIssued: new Date().toISOString().split('T')[0], // Default to today's date for new bids
      });
    }
  }, [bidType, bid, reset]);

  // Fetch clients when modal is opened
  useEffect(() => {
    if (showClientAddressBook && user) {
      const fetchClients = async () => {
        try {
          setIsLoadingClients(true);
          const fetchedClients = await getContacts(user.uid, 'client');
          setClients(fetchedClients);
        } catch (error) {
          console.error('BidForm: Error fetching clients:', error);
        } finally {
          setIsLoadingClients(false);
        }
      };
      
      fetchClients();
    }
  }, [showClientAddressBook, user]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters to clients
  const filteredClients = useMemo(() => {
    // Sort clients by name (case-insensitive)
    const sortedClients = [...clients].sort((a, b) => {
      const nameA = (a.name || a.company || '').toLowerCase();
      const nameB = (b.name || b.company || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return sortedClients.filter(client => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const clientValues = [
          client.name, 
          client.company, 
          client.email, 
          client.phone
        ].filter(Boolean).map(val => val.toLowerCase());
        const matchesSearch = clientValues.some(val => val.includes(searchTerm));
        if (!matchesSearch) return false;
      }
      return true;
    });
  }, [clients, filters]);

  // Handle selecting a client from the modal
  const handleSelectClient = (client) => {
    // Set Client Name and Contact
    setValue('ClientName', client.name || (client.company ? client.company : ''));
    setValue('Contact', client.email || client.phone || '');
    
    // Close the modal
    setShowClientAddressBook(false);
    
    // Reset filters for next time
    setFilters({ search: '' });
  };

  // Status options
  const statusOptions = [
    "Open", 
    "Submitted", 
    "Awarded", 
    "Lost", 
    "On Hold"
  ];

  const onFormSubmit = (data) => {
    // If not in edit mode, use bidType to determine if this is a project or standalone bid
    let formattedData;
    
    if (isEditMode) {
      // Preserve the original bid's type and ID for editing
      formattedData = {
        ...data,
        type: bid.type, // Preserve the type
        BidAmount: parseFloat(data.BidAmount) || 0,
        BidStatus: data.Status || 'Open', // Ensure BidStatus is set from Status
      };
    } else {
      // New bid - determine type from bidType
      const bidTypeForStore = bidType === 'project' ? 'project' : 'standalone';
      
      formattedData = {
        ...data,
        type: bidTypeForStore,
        BidAmount: parseFloat(data.BidAmount) || 0,
        BidStatus: data.Status || 'Open', // Ensure BidStatus is set from Status
      };
    }
    
    // Remove the temporary Status field to avoid confusion
    delete formattedData.Status;
    console.log('BidForm: Submitting form with data:', formattedData);
    onSubmit(formattedData);
  };

  return (
    <div className={isModal ? modalStyles.modalContent : styles.formContainer}>
      <button 
        type="button" 
        onClick={() => setShowClientAddressBook(true)} 
        className={isModal ? modalStyles.secondaryButton : styles.selectClientButton}
        style={{ marginBottom: '16px' }}
      >
        Select Client
      </button>
      
      <form onSubmit={handleSubmit(onFormSubmit)} className={styles.formTwoColumns}>
        {/* Left Column */}
        <div className={styles.columnLeft}>
          {(bidType === 'project' || (isEditMode && bid.type === 'project')) && (
            <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
              <label>Project #</label>
              <input {...register('JobNumber', { required: 'Project # is required' })} placeholder="e.g., P001" />
              {errors.JobNumber && <span className={styles.error}>{errors.JobNumber.message}</span>}
            </div>
          )}
          {(bidType === 'standalone' || (isEditMode && bid.type === 'standalone')) && (
            <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
              <label>Project #</label>
              <input {...register('JobNumber')} placeholder="e.g., S001" />
            </div>
          )}
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Client Name</label>
            <input {...register('ClientName', { required: 'Client Name is required' })} placeholder="e.g., Client A" />
            {errors.ClientName && <span className={styles.error}>{errors.ClientName.message}</span>}
          </div>
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Project Name</label>
            <input {...register('JobName', { required: 'Project Name is required' })} placeholder="e.g., PHL Airport" />
            {errors.JobName && <span className={styles.error}>{errors.JobName.message}</span>}
          </div>
          {(bidType === 'standalone' || (isEditMode && bid.type === 'standalone')) && (
            <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
              <label>Description</label>
              <input {...register('JobDescription')} placeholder="e.g., Standalone Project" />
              {errors.JobDescription && <span className={styles.error}>{errors.JobDescription.message}</span>}
            </div>
          )}
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Assigned To</label>
            <input {...register('AssignedTo')} placeholder="e.g., John Doe" />
            {errors.AssignedTo && <span className={styles.error}>{errors.AssignedTo.message}</span>}
          </div>
        </div>
        
        {/* Right Column */}
        <div className={styles.columnRight}>
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Contact</label>
            <input {...register('Contact')} placeholder="e.g., john@clienta.com" />
            {errors.Contact && <span className={styles.error}>{errors.Contact.message}</span>}
          </div>
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Date Due</label>
            <input
              type="date"
              {...register('DateDue')}
            />
            {errors.DateDue && <span className={styles.error}>{errors.DateDue.message}</span>}
          </div>
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Date Issued</label>
            <input
              type="date"
              {...register('DateIssued', { required: 'Date Issued is required' })}
            />
            {errors.DateIssued && <span className={styles.error}>{errors.DateIssued.message}</span>}
          </div>
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Status</label>
            <select 
              name="Status" 
              {...register("Status", { required: "Status is required" })}
              className={errors.Status ? styles.inputError : ""}
            >
              <option value="">Select Status</option>
              {statusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {errors.Status && <span className={styles.error}>{errors.Status.message}</span>}
          </div>
          <div className={isModal ? modalStyles.formGroup : styles.formGroup}>
            <label>Proposed Amount</label>
            <input type="number" {...register('BidAmount', { required: 'Proposed Amount is required', min: 0 })} placeholder="e.g., 50000" />
            {errors.BidAmount && <span className={styles.error}>{errors.BidAmount.message}</span>}
          </div>
        </div>
        
        {/* Form Actions */}
        <div className={isModal ? modalStyles.formActions : styles.formActions}>
          <button 
            type="submit" 
            className={isModal ? modalStyles.primaryButton : styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Bid' : 'Create Bid')}
          </button>
          <button 
            type="button" 
            onClick={onCancel} 
            className={isModal ? modalStyles.secondaryButton : styles.cancelButton}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
      
      {/* Client Address Book Modal */}
      {showClientAddressBook && (
        <div className={modalStyles.modalOverlay}>
          <div className={modalStyles.modalContent}>
            {/* X Close Button at top right */}
            <button
              type="button"
              onClick={() => {
                setShowClientAddressBook(false);
                setFilters({ search: '' });
              }}
              className={modalStyles.closeButton}
              aria-label="Close"
              style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', color: 'white', cursor: 'pointer', zIndex: 2 }}
            >
              &times;
            </button>
            <h3 className={styles.modalTitle}>Client Address Book</h3>
            {/* Filters for the client list */}
            <div className={modalStyles.formGroup}>
              {/* Search Bar */}
              <input 
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search clients..."
                className={modalStyles.inputField}
              />
            </div>
            {isLoadingClients ? (
              <div className={styles.loading}>Loading clients...</div>
            ) : clients.length === 0 ? (
              <div className={styles.empty}>No clients found.</div>
            ) : filteredClients.length === 0 ? (
              <div className={styles.empty}>No clients match your search.</div>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map(client => (
                      <tr key={client.id}>
                        <td>{client.name || '-'}</td>
                        <td>{client.email || '-'}</td>
                        <td>{client.phone || '-'}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleSelectClient(client)}
                            className={modalStyles.secondaryButton}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BidForm; 